import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { CompanyCard } from './CompanyCard';
import { companies } from '../data/companies';
import { Menu, X } from 'lucide-react';

export const Dashboard = ({ onBack }) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleCompanyClick = (companyId) => {
    navigate(`/company/${companyId}`);
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setIsMobileMenuOpen(false);
  };

  const filteredCompanies = activeFilter === 'all' 
    ? companies 
    : companies.filter(company => company.industries?.includes(activeFilter));

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="dashboard">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="fixed top-4 left-4 z-[60] lg:hidden w-10 h-10 bg-zinc-800 border border-white/10 rounded-lg flex items-center justify-center text-white"
        data-testid="mobile-menu-toggle"
      >
        {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMobileMenuOpen(false)}
            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Hidden on mobile by default */}
      <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} lg:block`}>
        <Sidebar 
          onBack={onBack} 
          isCollapsed={isCollapsed} 
          onToggleCollapse={toggleCollapse}
          activeFilter={activeFilter}
          onFilterChange={handleFilterChange}
          isMobile={isMobileMenuOpen}
        />
      </div>

      {/* Main Content */}
      <motion.main 
        className="min-h-screen p-4 pt-16 lg:pt-6 lg:p-6"
        animate={{ marginLeft: typeof window !== 'undefined' && window.innerWidth >= 1024 ? (isCollapsed ? 80 : 288) : 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Company Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6"
          data-testid="company-grid"
        >
          {filteredCompanies.length > 0 ? (
            filteredCompanies.map((company, index) => (
              <CompanyCard
                key={company.id}
                company={company}
                index={index}
                onClick={handleCompanyClick}
              />
            ))
          ) : (
            <div className="col-span-1 md:col-span-2 flex items-center justify-center h-[300px]">
              <p className="text-zinc-500 text-lg">No companies in this industry yet.</p>
            </div>
          )}
        </motion.div>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-12 lg:mt-16 pt-6 lg:pt-8 border-t border-white/5"
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <p className="text-xs text-zinc-600">
              Building the future, one venture at a time.
            </p>
            <p className="text-xs font-mono text-zinc-700">
              © 2024 Waterline Group. All rights reserved.
            </p>
          </div>
        </motion.footer>
      </motion.main>
    </div>
  );
};
